import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "cookie_consent_accepted";

export default function CookieBanner() {
  // Three phases: null = checking storage; "visible" = show; "leaving" = animating out; "hidden" = unmounted
  const [phase, setPhase] = useState(null);

  useEffect(() => {
    let accepted = null;
    try {
      accepted = window.localStorage.getItem(STORAGE_KEY);
    } catch {
      // localStorage unavailable (private mode etc.) — fall back to showing once per session
      accepted = window.sessionStorage?.getItem(STORAGE_KEY) || null;
    }
    if (accepted === "true") {
      setPhase("hidden");
    } else {
      // Defer to next frame so the entrance transition is observed
      requestAnimationFrame(() => setPhase("visible"));
    }
  }, []);

  const persist = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, "true");
    } catch {
      try { window.sessionStorage.setItem(STORAGE_KEY, "true"); } catch { /* noop */ }
    }
  };

  const accept = () => {
    persist();
    setPhase("leaving");
    setTimeout(() => setPhase("hidden"), 350);
  };

  const dismiss = () => {
    // Treat dismiss as an implicit accept so it doesn't keep nagging the user.
    accept();
  };

  if (phase === null || phase === "hidden") return null;

  const visible = phase === "visible";

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-[60] pointer-events-none px-4 pb-4 sm:px-6 sm:pb-6"
    >
      <div
        className={`pointer-events-auto mx-auto max-w-5xl bg-[#0F172A] text-gray-100 border border-white/10 shadow-2xl shadow-black/20 transition-all duration-300 ease-out ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}
        style={{ borderRadius: 2 }}
      >
        <div className="px-5 py-5 sm:px-7 sm:py-6 flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
          <div className="flex items-start gap-4 flex-1">
            <div className="shrink-0 w-9 h-9 rounded-full bg-[#C9A24E]/15 flex items-center justify-center mt-0.5">
              <Cookie size={18} className="text-[#C9A24E]" strokeWidth={1.6} />
            </div>
            <p className="text-[14px] sm:text-[15px] leading-relaxed text-gray-200">
              We use cookies to improve your experience. By continuing to visit this site you agree to our use of cookies.{" "}
              <Link
                to="/privacy"
                data-testid="cookie-banner-privacy-link"
                className="underline decoration-[#C9A24E]/60 underline-offset-4 hover:decoration-[#C9A24E] text-white transition"
              >
                Privacy Policy
              </Link>
              .
            </p>
          </div>

          <div className="flex items-center gap-3 md:shrink-0 self-end md:self-auto">
            <button
              type="button"
              onClick={accept}
              data-testid="cookie-banner-accept-button"
              className="inline-flex items-center gap-2 bg-[#C9A24E] text-[#0F172A] px-6 py-3 text-[13px] font-semibold tracking-[0.04em] uppercase transition hover:bg-[#B8884A] hover:shadow-lg hover:shadow-[#B8884A]/20"
              style={{ borderRadius: 2 }}
            >
              Accept
            </button>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Dismiss"
              data-testid="cookie-banner-dismiss-button"
              className="p-2 text-gray-400 hover:text-white transition"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
